import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import dayjs from 'dayjs';
import Markdown from 'react-markdown';
import { jwtDecode } from "jwt-decode";
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faThumbsUp,
  faThumbsDown,
  faFileAlt,
  faSearch,
  faDownload,
  faSpinner,
  faCalendarAlt,
  faFilter,
  faComments,
  faLayerGroup
} from "@fortawesome/free-solid-svg-icons";

import evaApi from '../../services/api/evaApi';
import useAppSession from '../../stores/useAppSession';
import DocsModal from "../DocsModal/DocsModal.jsx";

const PageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  padding: 20px 12px;
  background-color: #f2f2f2;
  min-height: 100vh;
  box-sizing: border-box;

  @media (min-width: 768px) {
    padding: 40px 24px;
  }
`;

const MainCard = styled.div`
  width: 100%;
  max-width: 1100px;
  background: #ffffff;
  border: 1px solid #eaeaea;
  border-radius: 6px;
  padding: 20px;
  box-sizing: border-box;

  @media (min-width: 768px) {
    padding: 32px;
  }
`;

const Title = styled.h1`
  font-size: 20px;
  font-weight: 600;
  color: #333;
  margin-bottom: 24px;
`;

const FilterCard = styled.div`
  background: #fafafa;
  border: 1px solid #eaeaea;
  border-radius: 6px;
  padding: 16px;
  margin-bottom: 32px;

  @media (min-width: 768px) {
    padding: 24px;
  }
`;

const FilterGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  align-items: flex-end;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1 1 100%; 

  @media (min-width: 600px) {
    flex: 1 1 calc(50% - 16px); 
  }

  @media (min-width: 1024px) {
    flex: 1; 
  }

  &.search-group {
    flex: 1 1 100%;
  }

  label {
    font-size: 13px;
    font-weight: 600;
    color: #666;
    text-transform: uppercase;
  }
`;

const StyledInput = styled.input`
  height: 40px;
  padding: 0 12px;
  border-radius: 6px;
  border: 1px solid #dcdcdc;
  font-size: 15px;
  outline: none;
  background: #fff;
  width: 100%;
  box-sizing: border-box;
  transition: border-color 0.2s;
  &:focus { border-color: #ff8f40; }
`;

const StyledSelect = styled.select`
  height: 40px;
  padding: 0 12px;
  border-radius: 6px;
  border: 1px solid #dcdcdc;
  background: white;
  font-size: 15px;
  outline: none;
  cursor: pointer;
  width: 100%;
  box-sizing: border-box;
  &:focus { border-color: #ff8f40; }
`;

const ActionButton = styled.button`
  height: 40px;
  padding: 0 20px;
  background-color: ${props => props.variant === 'primary' ? "#ff8f40" : "#333"};
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: opacity 0.2s;
  width: 100%; 

  @media (min-width: 600px) {
    width: auto;
  }

  &:hover { opacity: 0.9; }
`;

const DateHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 40px 0 20px;
  
  &:after {
    content: '';
    flex: 1;
    height: 1px;
    background: #e0e0e0;
  }

  h3 {
    font-size: 14px;
    font-weight: 700;
    color: #555;
    background: #f2f2f2;
    padding: 6px 16px;
    border-radius: 20px;
    margin: 0;
    border: 1px solid #e0e0e0;
  }
`;

const SessionContainer = styled.div`
  background: #ffffff;
  border: 1px solid #e1e1e1;
  border-left: 4px solid #ff8f40;
  border-radius: 6px;
  margin-bottom: 24px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.03);
  overflow: hidden;
`;

const SessionHeader = styled.div`
  background: #fafafa;
  padding: 12px 20px;
  border-bottom: 1px solid #f0f0f0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;

  .session-id {
    font-size: 12px;
    color: #888;
    font-family: monospace;
    display: flex;
    align-items: center;
    gap: 6px;
  }
`;

const ChannelBadge = styled.span`
  background: #333;
  color: #fff;
  font-size: 11px;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 4px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const MessageList = styled.div`
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  background-color: #fff;
`;

const MessageItem = styled.div`
  display: flex;
  flex-direction: column;
  padding-bottom: 16px;
  border-bottom: 1px dashed #eee;

  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }

  .meta-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;
  }

  .qa-block {
    margin-bottom: 12px;
    
    .label {
      font-size: 11px;
      font-weight: 700;
      color: #aaa;
      text-transform: uppercase;
      margin-bottom: 4px;
    }
    
    .text {
      font-size: 15px;
      color: #333;
      line-height: 1.5;
    }

    &.answer {
      background: #f9f9f9;
      padding: 12px;
      border-radius: 6px;
      border-left: 3px solid #ddd;
    }
  }
`;

const RatingBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 500;
`;

function decodeEnvironmentId(token) {
  try {
    const decoded = jwtDecode(token);
    return decoded?.user_access?.environment?.environmentid || null;
  } catch { return null; }
}

const Messageboard = ({ onLogout }) => {
  const { clientId, token, roles } = useAppSession();
  const [messages, setMessages] = useState([]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedChannel, setSelectedChannel] = useState('Todos');
  const [searchText, setSearchText] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedRating, setSelectedRating] = useState('all');
  const [docsModal, setDocsModal] = useState({ open: false, docs: null, message: null });

  const inactiveTimerRef = useRef(null);
  const inactivityLimit = 8 * 60 * 60 * 1000;

  useEffect(() => {
    const resetTimer = () => {
      if (inactiveTimerRef.current) clearTimeout(inactiveTimerRef.current);
      inactiveTimerRef.current = setTimeout(() => onLogout(), inactivityLimit);
    };
    resetTimer();
    const events = ["mousemove", "keydown", "click", "scroll"];
    events.forEach(ev => window.addEventListener(ev, resetTimer));
    return () => {
      clearTimeout(inactiveTimerRef.current);
      events.forEach(ev => window.removeEventListener(ev, resetTimer));
    };
  }, [onLogout]);

  useEffect(() => {
    const today = new Date();
    const pastDate = new Date(today);
    pastDate.setDate(today.getDate() - 15);
    setEndDate(today.toISOString().split('T')[0]);
    setStartDate(pastDate.toISOString().split('T')[0]);
  }, []);

  const fetchMessages = async () => {
    if (!startDate || !endDate) return;
    setLoading(true);
    try {
      const response = await evaApi.getGroupedMessages(clientId, startDate, endDate);
      if (response.status === 401) onLogout();
      setMessages(response.data.messages || []);
    } catch (error) {
      toast.error("Erro ao carregar histórico.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMessages();
  }, [startDate, endDate, clientId]);

  const getChannelFromKey = (key) => {
    const k = key.toUpperCase();
    if (k.includes('WEB')) return 'Web';
    if (k.includes('WIDGET')) return 'Widget';
    if (k.includes('TELEGRAM')) return 'Telegram';
    if (k.includes('WHATSAPP')) return 'Whatsapp';
    return 'Outros';
  };

  const filteredMessages = Object.entries(messages).reduce((acc, [date, sessions]) => {
    const filteredSessions = Object.entries(sessions).reduce((sessionAcc, [sessionKey, messageArray]) => {
      const filteredArray = messageArray.filter((msg) => {
        const channel = getChannelFromKey(sessionKey);
        const matchesChannel = selectedChannel === 'Todos' || channel === selectedChannel;
        const matchesSearch = !searchText ||
          msg.pergunta.toLowerCase().includes(searchText.toLowerCase()) ||
          msg.resposta.toLowerCase().includes(searchText.toLowerCase());

        let matchesRating = true;
        if (selectedRating === 'null') matchesRating = msg.rating === null;
        else if (selectedRating === '1') matchesRating = msg.rating === 1;
        else if (selectedRating === '0') matchesRating = msg.rating === 0;

        return matchesChannel && matchesSearch && matchesRating;
      });
      if (filteredArray.length > 0) sessionAcc[sessionKey] = filteredArray;
      return sessionAcc;
    }, {});
    if (Object.keys(filteredSessions).length > 0) acc[date] = filteredSessions;
    return acc;
  }, {});

  const handleOpenDocs = async (item) => {
    try {
      const payload = { environmentid: decodeEnvironmentId(token), id: item.id, sessionid: item.sessionid };
      const response = await evaApi.getDocs(payload);
      if (response?.data?.length > 0) {
        setDocsModal({ open: true, docs: response.data[0], message: { text: item.resposta, id: item.id } });
      } else {
        toast.warn("Nenhum documento de origem encontrado.");
      }
    } catch (err) {
      console.error(err);
      toast.error("Erro ao buscar documentos.");
    }
  };

  const exportMessagesToCSV = () => {
    const rows = [['Data', 'Sessão', 'Rating', 'Pergunta', 'Resposta']];
    Object.entries(filteredMessages).forEach(([date, sessions]) => {
      Object.entries(sessions).forEach(([session, messagesArray]) => {
        messagesArray.forEach((msg) => {
          const ratingText = msg.rating === 1 ? 'Positivo' : msg.rating === 0 ? 'Negativo' : 'Não avaliado';
          rows.push([msg.data, session, ratingText, msg.pergunta, msg.resposta]);
        });
      });
    });
    const csvContent = "data:text/csv;charset=utf-8,\uFEFF" + rows.map(e => e.map(v => `"${v}"`).join(",")).join("\n");
    const link = document.createElement("a");
    link.setAttribute("href", encodeURI(csvContent));
    link.setAttribute("download", `historico_${dayjs().format('YYYYMMDD')}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <PageWrapper>
      <MainCard>
        <Title>Monitoramento de Conversas</Title>

        <FilterCard>
          <FilterGrid>
            <FormGroup className="search-group">
              <label><FontAwesomeIcon icon={faSearch} /> Pesquisa</label>
              <StyledInput
                placeholder="Filtrar por palavras..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />
            </FormGroup>

            <FormGroup>
              <label><FontAwesomeIcon icon={faFilter} /> Canal</label>
              <StyledSelect value={selectedChannel} onChange={(e) => setSelectedChannel(e.target.value)}>
                <option value="Todos">Todos os Canais</option>
                <option value="Telegram">Telegram</option>
                <option value="Web">Web</option>
                <option value="Whatsapp">Whatsapp</option>
                <option value="Widget">Widget</option>
              </StyledSelect>
            </FormGroup>

            <FormGroup>
              <label>Avaliação</label>
              <StyledSelect value={selectedRating} onChange={(e) => setSelectedRating(e.target.value)}>
                <option value="all">Todos os Ratings</option>
                <option value="null">Não avaliado</option>
                <option value="1">Positivo</option>
                <option value="0">Negativo</option>
              </StyledSelect>
            </FormGroup>

            <FormGroup>
              <label><FontAwesomeIcon icon={faCalendarAlt} /> Início</label>
              <StyledInput type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            </FormGroup>

            <FormGroup>
              <label><FontAwesomeIcon icon={faCalendarAlt} /> Fim</label>
              <StyledInput type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
            </FormGroup>

            <ActionButton variant="primary" onClick={exportMessagesToCSV}>
              <FontAwesomeIcon icon={faDownload} /> Exportar
            </ActionButton>
          </FilterGrid>
        </FilterCard>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '60px' }}>
            <FontAwesomeIcon icon={faSpinner} spin size="2x" color="#ff8f40" />
            <p style={{ marginTop: '16px', color: '#666' }}>Carregando histórico...</p>
          </div>
        ) : (
          <div>
            {Object.keys(filteredMessages).length === 0 && (
              <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>Nenhuma mensagem encontrada.</div>
            )}

            {Object.entries(filteredMessages).map(([data, sessions]) => (
              <div key={data}>
                <DateHeader>
                  <h3>{dayjs(data).format('DD/MM/YYYY')}</h3>
                </DateHeader>

                {Object.entries(sessions).map(([session, messagesArray]) => (
                  <SessionContainer key={session}>
                    <SessionHeader>
                      <ChannelBadge>
                        {getChannelFromKey(session)}
                      </ChannelBadge>
                      <span className="session-id">
                        <FontAwesomeIcon icon={faLayerGroup} />
                        ID: {session}
                      </span>
                    </SessionHeader>

                    <MessageList>
                      {messagesArray.map((item, index) => (
                        <MessageItem key={index}>
                          <div className="meta-row">
                            <RatingBadge>
                              {item.rating === 1 && <FontAwesomeIcon icon={faThumbsUp} style={{ color: "#34c759" }} />}
                              {item.rating === 0 && <FontAwesomeIcon icon={faThumbsDown} style={{ color: "#ff3b30" }} />}
                              {item.rating === null && <span style={{ color: '#ccc', fontSize: '11px' }}>Não avaliado</span>}
                            </RatingBadge>

                            {roles?.some(r => ["ADMIN", "ADMIN_IA", "USER"].includes(String(r).toUpperCase())) && (
                              <button
                                onClick={() => handleOpenDocs(item)}
                                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ff8f40', fontSize: '14px' }}
                                title="Ver Fontes"
                              >
                                <FontAwesomeIcon icon={faFileAlt} />
                              </button>
                            )}
                          </div>

                          <div className="qa-block">
                            <div className="label">Usuário</div>
                            <div className="text">{item.pergunta}</div>
                          </div>

                          <div className="qa-block answer">
                            <div className="label">EVA AI</div>
                            <div className="text">
                              <Markdown>{item.resposta}</Markdown>
                            </div>
                          </div>
                        </MessageItem>
                      ))}
                    </MessageList>
                  </SessionContainer>
                ))}
              </div>
            ))}
          </div>
        )}
      </MainCard>

      {docsModal.open && (
        <DocsModal
          open={docsModal.open}
          onClose={() => setDocsModal({ open: false, docs: null, message: null })}
          data={docsModal.docs}
          message={docsModal.message}
        />
      )}
    </PageWrapper>
  );
};

export default Messageboard;